import React, { useEffect } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import Banner from 'components/Banner';
import BannerAcceptComCom from 'components/BannerAcceptComCom';
import BannerNewUser from 'components/BannerNewUser';
import NavBar from 'components/NavBar';
import NotificationsBar from 'components/NotificationsBar';
import Container from 'components/Container';
import { CloseButton } from 'components/Notification/Notification';
import { ROUTES, MOBILE_ALLOWED_ROUTES } from 'utils/constants/routes';
import Login from 'pages/Login';
import Offers from 'pages/Offers';
import OfferDetail from 'pages/OfferDetail';
import PendingJobs from 'pages/PendingJobs';
import PendingJobDetail from 'pages/PendingJobDetail';
import InTransitJobs from 'pages/InTransitJobs';
import DoneJobs from 'pages/DoneJobs';
import InTransitJobDetail from 'pages/InTransitJobDetail';
import RecoverPassword from 'pages/RecoverPassword';
import RecoverPwdLanding from 'pages/RecoverPwdLanding';
import DownloadMobileApp from 'pages/DownloadMobileApp';
import AccountSettings from 'pages/AccountSettings';
import PendingPods from 'pages/PendingPods';
import JobDetail from 'pages/JobDetail';
import { useAuth } from 'hooks/useAuth/useAuth';
import { createBrowserHistory } from 'history';
import Logout from 'pages/Logout/Logout';
import Chat from 'pages/Chat';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ProvideOffers } from 'hooks/useOffers/useOffers';
import { ProvidePendingJobs } from 'hooks/usePendingJobs/usePendingJobs';
import { ProvideInTransitJobs } from 'hooks/useInTransitJobs/useInTransitJobs';
import { ProvideDoneJobs } from 'hooks/useDoneJobs/useDoneJobs';
import { ProvideVehicles } from 'hooks/useVehicles/useVehicles';
import { sendEventToSegment, setSegmentPage } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import queryString from 'query-string';
import { IS_MOBILE } from 'utils/constants/browser';
import { ProvideAlert } from 'hooks/useAlert/useAlert';
import { ProvidePendingPods } from 'hooks/usePendingPods/usePendingPods';
import Refiner from 'refiner-js';
import Documents from 'pages/Documents';
import DocumentsWelcome from 'modules/Documentation/DocumentsWelcome';
import DocumentsBillingData from 'modules/Documentation/DocumentsBillingData';
import DocumentsList from 'modules/Documentation/DocumentsList';
import { ProvideDocsCarrierProfile } from 'hooks/useDocsCarrierProfile/useDocsCarrierProfile';

export const history = createBrowserHistory();

const Routes = () => {
  const {
    shouldSetPassword,
    commercialCommunicationsAccepted,
    profile,
  } = useAuth();

  useEffect(() => {
    const unregisterHistoryListener = history.listen((location) => {
      setSegmentPage();
      if (profile) {
        sendEventToSegment(TRACKING_EVENTS.PAGE_VIEW, {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        });
      }
      window.scrollTo(0, 0);
    });
    return () => unregisterHistoryListener();
  }, [profile]);

  useEffect(() => {
    if (profile) {
      Refiner('setProject', 'e135c840-db9d-11ea-b5f0-fb3eb26dc545');
      Refiner('identifyUser', {
        type: 'CR',
        id: profile.driver.id?.toString(),
        name: profile.driver.contact_name,
        country: profile.carrier.country,
        language: profile.user.country_settings.locale,
        created_at: profile.user.created_at,
        account: {
          id: profile.carrier.id?.toString(),
          name: profile.carrier.name,
          created_at: profile.carrier.created_at,
        },
      });
    }
  }, [profile]);

  return (
    <Router history={history}>
      <ProvideAlert>
        <ProvidePendingPods>
          <ProvideDocsCarrierProfile>
            <ToastContainer
              toastClassName="toast"
              bodyClassName="toast__body"
              progressClassName="toast__progress"
              position="top-left"
              closeButton={CloseButton}
            />
            <NavBar />
            <Container>
              <NotificationsBar />
              <Switch>
                <Route exact path={ROUTES.DOWNLOAD_MOBILE_APP}>
                  <DownloadMobileApp />
                </Route>
                <Route path={ROUTES.LOGIN} component={Login} />
                <Route path={ROUTES.LOGOUT} component={Logout} />
                <Route
                  path={ROUTES.PASSWORD_RECOVERY}
                  component={RecoverPassword}
                />
                <Route
                  path={ROUTES.PASSWORD_RESET}
                  component={RecoverPwdLanding}
                />
                <PrivateRoute exact path={ROUTES.DOCS.PARAM}>
                  <Documents />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.DOCS_WELCOME.PARAM}>
                  <DocumentsWelcome />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.DOCS_BILLING_DATA.PARAM}>
                  <DocumentsBillingData />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.DOCS_DOCUMENTS.PARAM}>
                  <DocumentsList />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.OFFERS} withAutoLogin>
                  <ProvideOffers pollingActivated={true}>
                    <Offers />
                  </ProvideOffers>
                </PrivateRoute>
                <PrivateRoute
                  exact
                  path={ROUTES.OFFER_DETAIL.PARAM}
                  withAutoLogin
                >
                  <ProvideOffers>
                    <OfferDetail />
                  </ProvideOffers>
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.PENDING}>
                  <ProvidePendingJobs pollingActivated={true}>
                    <PendingJobs />
                  </ProvidePendingJobs>
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.PENDING_DETAIL.PARAM}>
                  <ProvideVehicles>
                    <PendingJobDetail />
                  </ProvideVehicles>
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.IN_TRANSIT}>
                  <ProvideInTransitJobs>
                    <InTransitJobs />
                  </ProvideInTransitJobs>
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.DONE}>
                  <ProvideDoneJobs>
                    <DoneJobs />
                  </ProvideDoneJobs>
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.IN_TRANSIT_DETAIL.PARAM}>
                  <InTransitJobDetail />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.PENDING_PODS_DETAIL.PARAM}>
                  <JobDetail />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.DONE_DETAIL.PARAM}>
                  <JobDetail />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.ACCOUNT_SETTINGS}>
                  <AccountSettings />
                </PrivateRoute>
                <PrivateRoute exact path={ROUTES.PENDING_PODS}>
                  <PendingPods />
                </PrivateRoute>
                <PrivateRoute>
                  <Redirect
                    to={{
                      pathname: ROUTES.OFFERS,
                    }}
                  />
                </PrivateRoute>
              </Switch>
            </Container>
            <Chat />
            <Banner>
              <BannerNewUser name="new-user" shouldShow={shouldSetPassword} />
              <BannerAcceptComCom
                name="accept-commercial-communications"
                shouldShow={commercialCommunicationsAccepted === null}
                preserve={true}
              />
            </Banner>
          </ProvideDocsCarrierProfile>
        </ProvidePendingPods>
      </ProvideAlert>
    </Router>
  );
};

const PrivateRoute = ({ children, withAutoLogin, ...rest }) => {
  const auth = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) => {
        const { t, source } = queryString.parse(location.search);
        if (auth.profile) {
          if (
            IS_MOBILE &&
            !MOBILE_ALLOWED_ROUTES.find((route) =>
              route.includes(location.pathname.split('/')[1])
            )
          ) {
            return <Redirect to={{ pathname: ROUTES.DOWNLOAD_MOBILE_APP }} />;
          } else {
            return children;
          }
        } else if (withAutoLogin) {
          return (
            <Redirect
              to={{
                pathname: ROUTES.LOGIN,
                state: { from: location, queryToken: t, source: source },
              }}
            />
          );
        } else {
          return (
            <Redirect
              to={{
                pathname: ROUTES.LOGIN,
                state: { from: location, source: source },
              }}
            />
          );
        }
      }}
    />
  );
};

export default Routes;
